import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { ExportType } from 'cli/enums';
import { MenuButton } from 'core/elements';
import { ApiStatus, ExportEntity, FileType, NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { useAgGridClientTableContext, useNotificationContext } from 'core/states';
import { isEmptyArray } from 'core/utils';
import { usePartActions } from 'parts/hooks';
import { ExportSkuPartMenuItem, ExportSkuPartSteps } from 'quotes/components/ExportSkuPart';
import { QuoteEntityType, QuotePartListTriggerAction, QuoteType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { IBaseQuote, IQuotePart, IVendorQuote } from 'quotes/types';
import { getActiveQuoteParts } from 'quotes/utils';
import { Color, Variant } from 'theme/enums';

import { QuotePartExportType } from './enums';
import { IQuoteDownloadOptions } from './types';

interface IProps {
  quote: IBaseQuote;
  quoteType: QuoteType;
  quoteEntityType: QuoteEntityType;
  trigger?: QuotePartListTriggerAction;
}

export const DownloadButton = ({
  quote,
  quoteType = QuoteType.CUSTOMER,
  quoteEntityType = QuoteEntityType.QUOTE,
  trigger = null,
}: IProps) => {
  const { t } = useTranslation();

  const [isExportSkuPartOpen, setIsExportSkuPartOpen] = useState(false);

  const { showNotification } = useNotificationContext();
  const { rows, isDataLoadCompleted } = useAgGridClientTableContext();

  const { user } = useAuthUser();
  const { handleCliJobExport } = useCliJobHandler();

  const { exportQuote } = useQuoteActions();
  const { exportPartDatasheets } = usePartActions();

  const activeRowData = getActiveQuoteParts(rows as IQuotePart[]);

  const downloadQuote = useCallback(
    (fileType: FileType, exportType = ExportEntity.QUOTE, options: IQuoteDownloadOptions = {}) => {
      const exportData = {
        quoteId: quote?.id,
        exportType,
        options: {
          fileType,
          quoteType,
          ...options,
        },
      };

      const exportOptions = {
        exportInProgressMessage: t('messageGenerateExportFileTypeInProgress', {
          fileType: fileType.toUpperCase(),
        }),
        exportSuccessMessage: t('messageGenerateExportFileTypeSuccess', {
          fileType: fileType.toUpperCase(),
        }),
      };

      handleCliJobExport(exportQuote, exportData, exportOptions);
    },
    [quote, quoteType, t, handleCliJobExport, exportQuote]
  );

  useEffect(() => {
    if (trigger === QuotePartListTriggerAction.DOWNLOAD_XLS) {
      downloadQuote(FileType.XLS);
    }

    if (trigger === QuotePartListTriggerAction.DOWNLOAD_PDF) {
      downloadQuote(FileType.PDF);
    }
  }, [trigger, downloadQuote]);

  const downloadDataSheetArchive = (rowData = []) => {
    const activeQuotePartIds = rowData?.map((quotePart) => {
      if (1 === quotePart.verified && 0 === quotePart.deleted) {
        return quotePart['partId'];
      }
    });

    if (isEmptyArray(activeQuotePartIds)) {
      showNotification({
        title: t('titleError'),
        type: ApiStatus.ERROR,
        message: t('errorNoPartsProvidedForDatasheetExport'),
        position: NotificationPosition.TOP,
      });

      return;
    }
    const exportData = { partIds: activeQuotePartIds };

    const exportOptions = {
      exportInProgressMessage: t('messageGeneratePartsDatasheetArchiveInProgress'),
      exportSuccessMessage: t('messageGeneratePartsDatasheetArchiveSuccess'),
    };

    handleCliJobExport(exportPartDatasheets, exportData, exportOptions, ExportType.ARCHIVE);
  };

  if (QuoteEntityType.ORDER === quoteEntityType) {
    return null;
  }

  return (
    <>
      <MenuButton
        label={t('buttonLabelDownload')}
        disabled={activeRowData?.length === 0}
        color={Color.PRIMARY}
        variant={Variant.SOFT}
        menuItems={[
          {
            label: t('buttonLabelDownloadXls'),
            onClick: () => downloadQuote(FileType.XLS),
          },
          {
            label: t('buttonLabelDownloadPdf'),
            onClick: () => downloadQuote(FileType.PDF),
          },
          {
            label: t('buttonLabelDownloadDataSheets'),
            onClick: () => downloadDataSheetArchive(activeRowData),
            disabled: !isDataLoadCompleted,
          },
          {
            label: t('buttonLabelExportQuoteParts'),
            onClick: () =>
              downloadQuote(FileType.XLS, ExportEntity.QUOTE_PARTS, {
                partExportType: QuotePartExportType.LEGACY,
              }),
            hide: !user.companySettings.vendorQuotePartsExportEnabled,
          },
          {
            label: 'exportSkuParts',
            component: (
              <ExportSkuPartMenuItem
                key="exportSkuParts"
                quote={quote as IVendorQuote}
                onOpen={() => setIsExportSkuPartOpen(true)}
              />
            ),
          },
        ]}
      />

      <ExportSkuPartSteps
        quote={quote as IVendorQuote}
        isOpen={isExportSkuPartOpen}
        onClose={() => setIsExportSkuPartOpen(false)}
      />
    </>
  );
};
